/**
 * auth-client — React context + hook for authentication state.
 * Wraps login, logout, and token storage so components are auth-aware.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { apiClient } from '../api-client';
import { cancelAccountDeletion, type PendingDeletion } from '../api-client/account-deletion-api';

export interface AuthUser {
    user_id: string;
    tenant_id: string;
    email: string;
    roles: string[];
    is_superadmin?: boolean;
    // Access scopes (from the JWT, surfaced on /auth/me). A restricted user is
    // limited to projects they belong to / tasks they own, and gets a stripped
    // -down UI (minimal sidebar, fewer project tabs, project-first landing).
    is_project_restricted?: boolean;
    is_task_restricted?: boolean;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    impersonated_by?: string; // present only when impersonating
    // Present only while the account is inside its 30-day deletion window.
    // Which sign-in response carries it depends on whether the tenant has OTP
    // on — the direct path ends at /auth/login, the OTP path at
    // /auth/otp/verify, and /auth/me is what a page reload re-reads. Reading it
    // in one place only would make the banner silently never appear on the
    // other paths, so the provider merges whichever arrives.
    pending_deletion?: PendingDeletion | null;
}

export interface ImpersonationTarget {
    id: string;
    email: string;
    tenant_id?: string;
    // Chosen on ImpersonatePage; the API returns only the id.
    tenant_name?: string;
}

// Keys that hold a stashed operator session or the effective identity. None
// may outlive the impersonation they belong to.
const IMPERSONATION_KEYS = ['admin_token', 'admin_refresh_token', 'impersonation_target'];

// True when a JWT's exp is in the past. Undecodable counts as unexpired: the
// server is the judge, this only avoids a request that is certain to 401.
function jwtExpired(token: string): boolean {
    try {
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(b64)) as { exp?: number };
        return typeof exp === 'number' && exp * 1000 <= Date.now();
    } catch {
        return false;
    }
}

interface AuthContextValue {
    user: AuthUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isImpersonating: boolean;
    impersonationTarget: ImpersonationTarget | null;
    otpPending: boolean;           // true when password is verified but OTP not yet entered
    // Non-null while the signed-in account is scheduled for deletion. Drives
    // the recovery banner; cancelDeletion() is the way back out.
    pendingDeletion: PendingDeletion | null;
    login: (email: string, password: string) => Promise<'otp_required' | 'authenticated'>;
    verifyOTP: (code: string, rememberDevice?: boolean) => Promise<void>;
    resendOTP: () => Promise<void>;
    /** Ends the session, then does a full navigation to /login (no returnTo:
     *  the next person to sign in must not land on this user's page). Pass
     *  { redirect: false } only from a public page that navigates itself. */
    logout: (opts?: { redirect?: boolean }) => Promise<void>;
    cancelDeletion: () => Promise<void>;
    refreshUser: () => Promise<void>;
    startImpersonation: (userId: string, opts?: { reason?: string; tenantName?: string }) => Promise<void>;
    // Audits the exit (best effort), restores the operator's tokens and
    // clears every cached query. Callers then do a full reload.
    exitImpersonation: () => Promise<void>;
    isSuperAdmin: () => boolean;
    // isRestricted is true when the user is project- or task-restricted — the
    // single switch the UI uses to render the locked-down experience.
    isRestricted: () => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionToken, setSessionToken] = useState<string | null>(null);
    const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
    // Optional: tests may render the provider without a QueryClientProvider.
    const queryClient = useContext(QueryClientContext);
    // UI-01: server data cached for one identity must never be shown to the
    // next. Called on every identity change: sign-in, sign-out, and entering
    // or leaving impersonation.
    const resetCache = useCallback(() => { queryClient?.clear(); }, [queryClient]);
    const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(() => {
        const saved = localStorage.getItem('impersonation_target');
        return saved ? JSON.parse(saved) : null;
    });

    // Fetch full profile (/auth/me + /users/me) and merge into state.
    const loadUser = useCallback(async () => {
        const [meRes, profileRes] = await Promise.all([
            apiClient.get<AuthUser>('/auth/me'),
            apiClient.get<{ first_name: string; last_name: string; avatar_url?: string }>('/users/me'),
        ]);
        setUser({
            ...meRes.data,
            first_name: profileRes.data.first_name,
            last_name: profileRes.data.last_name,
            avatar_url: profileRes.data.avatar_url,
        });
        // Merge rather than overwrite: /auth/me may not be the response that
        // carries pending_deletion, and a sign-in that already found it must
        // not have it cleared by the profile fetch that follows.
        if (meRes.data.pending_deletion) setPendingDeletion(meRes.data.pending_deletion);
    }, []);

    // On mount, try to load user from persisted token
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (token) {
            loadUser()
                .catch(() => {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    // A dead impersonation token (expired) ends the whole
                    // session: the operator signs in again rather than the
                    // stashed operator tokens lingering in this browser.
                    IMPERSONATION_KEYS.forEach(k => localStorage.removeItem(k));
                    setImpersonationTarget(null);
                })
                .finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
    }, [loadUser]);

    const login = useCallback(async (email: string, password: string): Promise<'otp_required' | 'authenticated'> => {
        const { data } = await apiClient.post<{
            otp_required: boolean;
            session_token?: string;
            access_token?: string;
            refresh_token?: string;
            pending_deletion?: PendingDeletion | null;
        }>('/auth/login', { email, password }, { withCredentials: true }); // send td_token cookie

        // A scheduled deletion does not block sign-in — it is the only way back
        // out, so the account has to let the owner in to change their mind.
        if (data.pending_deletion) setPendingDeletion(data.pending_deletion);

        if (data.otp_required && data.session_token) {
            setSessionToken(data.session_token);
            return 'otp_required';
        }

        // OTP not required — immediately authenticated (legacy / future toggle)
        localStorage.setItem('access_token', data.access_token!);
        localStorage.setItem('refresh_token', data.refresh_token!);
        resetCache();
        await loadUser();
        return 'authenticated';
    }, [loadUser, resetCache]);

    const verifyOTP = useCallback(async (code: string, rememberDevice = false): Promise<void> => {
        if (!sessionToken) throw new Error('No active OTP session');
        const { data } = await apiClient.post<{
            access_token: string;
            refresh_token: string;
            pending_deletion?: PendingDeletion | null;
        }>(
            '/auth/otp/verify',
            { session_token: sessionToken, code, remember_device: rememberDevice },
            { withCredentials: true }, // required for td_token cookie
        );
        if (data.pending_deletion) setPendingDeletion(data.pending_deletion);
        setSessionToken(null);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        resetCache();
        await loadUser();
    }, [sessionToken, loadUser, resetCache]);

    const resendOTP = useCallback(async (): Promise<void> => {
        if (!sessionToken) throw new Error('No active OTP session');
        await apiClient.post('/auth/otp/resend', { session_token: sessionToken });
    }, [sessionToken]);

    /** Re-fetch the current user's profile (call after profile edits). */
    const refreshUser = useCallback(async () => {
        await loadUser();
    }, [loadUser]);

    const logout = useCallback(async ({ redirect = true }: { redirect?: boolean } = {}) => {
        const refreshToken = localStorage.getItem('refresh_token');
        try {
            // withCredentials: true ensures the td_token cookie is sent
            // so the backend can clear it server-side.
            await apiClient.post('/auth/logout', { refresh_token: refreshToken }, { withCredentials: true });
        } finally {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            IMPERSONATION_KEYS.forEach(k => localStorage.removeItem(k));
            setImpersonationTarget(null);
            setUser(null);
            setPendingDeletion(null);
            resetCache();
            // Centrally, so every sign-out button (sidebar, a new shell, the
            // deletion panel) lands on a clean /login rather than letting
            // ProtectedRoute record the page being left as returnTo.
            if (redirect) window.location.replace('/login');
        }
    }, [resetCache]);

    /** POST /me/cancel-deletion — the way back out of the 30-day window. */
    const cancelDeletion = useCallback(async () => {
        await cancelAccountDeletion();
        setPendingDeletion(null);
    }, []);

    const startImpersonation = useCallback(async (userId: string, opts: { reason?: string; tenantName?: string } = {}) => {
        const { data } = await apiClient.post<{ access_token: string; target_user: ImpersonationTarget }>(
            `/auth/impersonate/${userId}`, { reason: opts.reason ?? '' },
        );
        // Save admin tokens before swapping
        localStorage.setItem('admin_token', localStorage.getItem('access_token') ?? '');
        localStorage.setItem('admin_refresh_token', localStorage.getItem('refresh_token') ?? '');
        // Swap to impersonation token
        localStorage.setItem('access_token', data.access_token);
        localStorage.removeItem('refresh_token'); // no refresh for impersonation
        // Store target info for the banner
        const target: ImpersonationTarget = { ...data.target_user, tenant_name: opts.tenantName };
        localStorage.setItem('impersonation_target', JSON.stringify(target));
        setImpersonationTarget(target);
        resetCache();
        // Re-fetch /me with the new token
        await loadUser();
    }, [loadUser, resetCache]);

    const exitImpersonation = useCallback(async () => {
        // Audit the exit while the impersonation token is still the one the
        // request interceptor sends. Skipped when it has expired: a 401 here
        // would trigger the refresh path's redirect to /login mid-exit.
        const impToken = localStorage.getItem('access_token');
        if (impToken && !jwtExpired(impToken)) {
            try {
                // Bounded: a hung request must not keep the operator stuck
                // as the effective user.
                await apiClient.post('/auth/impersonate/exit', null, { timeout: 5000 });
            } catch { /* exit must not depend on the audit call */ }
        }
        const adminToken = localStorage.getItem('admin_token');
        const adminRefresh = localStorage.getItem('admin_refresh_token');
        IMPERSONATION_KEYS.forEach(k => localStorage.removeItem(k));
        setImpersonationTarget(null);
        resetCache();
        if (!adminToken) {
            // Nothing to restore: drop the effective user's token rather than
            // stay signed in as them.
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
            return;
        }
        localStorage.setItem('access_token', adminToken);
        if (adminRefresh) localStorage.setItem('refresh_token', adminRefresh);
        // Re-fetch /me to restore admin user state
        await loadUser().catch(() => {});
    }, [loadUser, resetCache]);

    const isSuperAdmin = useCallback(() => {
        // Sourced from users.is_superadmin via the JWT claim, surfaced on
        // /auth/me. Migration 000040 moved superadmin off the roles table,
        // so checking roles for the literal "superadmin" string is wrong.
        return user?.is_superadmin === true;
    }, [user]);

    const isRestricted = useCallback(() => {
        return user?.is_project_restricted === true || user?.is_task_restricted === true;
    }, [user]);

    const isImpersonating = !!(user?.impersonated_by);
    const otpPending = sessionToken !== null;

    return (
        <AuthContext.Provider
            value={{
                user, isLoading, isAuthenticated: !!user,
                isImpersonating, impersonationTarget,
                otpPending, pendingDeletion,
                login, verifyOTP, resendOTP,
                logout, cancelDeletion,
                refreshUser, startImpersonation, exitImpersonation, isSuperAdmin,
                isRestricted,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

/** Hook to access auth state from any component. */
export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
}
